// M17.4 — Dons passifs (PASSIFS.md). Auto-unlocked at BASE-stat thresholds
// (never effective stats — same rule as spells/equipment, so swapping a +stat
// ring can't bootstrap a don). The thresholds live in content (admin-tunable);
// each `effectKey` maps to a hard-coded hook on the server (same split as
// affixes: data = thresholds, code = effects). A don, once unlocked by a base
// stat that only ever rises, stays unlocked forever — nothing to persist
// except the set of dons a player has manually DISABLED (PASSIFS rule 4).

import { z } from "zod";
import type { CharacterStats } from "../types.js";

// The hooks the server knows how to apply. Adding a new don = a new key here
// + its hook in the sim. The MVP-8 (PASSIFS §6).
export type PassiveEffectKey =
  | "iron_shoulder" // STR 50 — swap place with an adjacent ally
  | "light_step" // DEX 50 — mob aggro range −20% against you
  | "iron_stomach" // CON 50 — potions +25% effect
  | "scholars_eye" // INT 50 — see mobs' exact level + HP numbers
  | "soothing_presence" // FAI 50 — mobs target you last
  | "initiative" // RAP 50 — first swing of each fight is 2× faster
  | "crusade" // STR 100 + FAI 100 — taunt mobs hitting nearby allies
  | "colossus_leap"; // CON 100 + RAP 100 — targetless leap (4 tiles)

export interface PassiveTemplate {
  id: string;
  name: string; // in-game EN name
  desc: string;
  // Base-stat thresholds (ALL must be met). A combo don has 2+ entries.
  requirements: Partial<Record<keyof CharacterStats, number>>;
  effectKey: PassiveEffectKey;
}

export const BUILTIN_PASSIVES: PassiveTemplate[] = [
  {
    id: "iron_shoulder",
    name: "Iron Shoulder",
    desc: "Swap places with an adjacent ally — the rescue manoeuvre.",
    requirements: { str: 50 },
    effectKey: "iron_shoulder",
  },
  {
    id: "light_step",
    name: "Light Step",
    desc: "Mobs' aggro range is reduced by 20% against you.",
    requirements: { dex: 50 },
    effectKey: "light_step",
  },
  {
    id: "iron_stomach",
    name: "Iron Stomach",
    desc: "Potions restore 25% more.",
    requirements: { con: 50 },
    effectKey: "iron_stomach",
  },
  {
    id: "scholars_eye",
    name: "Scholar's Eye",
    desc: "See the exact level and HP of mobs.",
    requirements: { int: 50 },
    effectKey: "scholars_eye",
  },
  {
    id: "soothing_presence",
    name: "Soothing Presence",
    desc: "Mobs target you last when choosing a victim.",
    requirements: { fai: 50 },
    effectKey: "soothing_presence",
  },
  {
    id: "initiative",
    name: "Initiative",
    desc: "Your first swing of each fight lands twice as fast.",
    requirements: { rap: 50 },
    effectKey: "initiative",
  },
  {
    id: "crusade",
    name: "Crusade",
    desc: "Mobs that strike an ally within 2 tiles target you instead.",
    requirements: { str: 100, fai: 100 },
    effectKey: "crusade",
  },
  {
    id: "colossus_leap",
    name: "Colossus Leap",
    desc: "Leap up to 4 tiles to a free spot, over bodies and low obstacles (≈45s cd).",
    requirements: { con: 100, rap: 100 },
    effectKey: "colossus_leap",
  },
];

// Runtime catalogue — swappable via setPassiveCatalogue (admin publish),
// mirroring the spell/affix registries.
let runtimePassives: PassiveTemplate[] = [...BUILTIN_PASSIVES];
const byId = new Map<string, PassiveTemplate>(BUILTIN_PASSIVES.map((p) => [p.id, p]));

function reindex(): void {
  byId.clear();
  for (const p of runtimePassives) byId.set(p.id, p);
}

export function getPassiveCatalogue(): readonly PassiveTemplate[] {
  return runtimePassives;
}

export function setPassiveCatalogue(list: readonly PassiveTemplate[]): void {
  runtimePassives = [...list];
  reindex();
}

export function _resetPassivesForTests(): void {
  runtimePassives = [...BUILTIN_PASSIVES];
  reindex();
}

export function getPassiveTemplate(id: string): PassiveTemplate | undefined {
  return byId.get(id);
}

// Unlock test — ALL requirements met against BASE stats (PASSIFS rule 1).
export function isPassiveUnlocked(p: PassiveTemplate, baseStats: CharacterStats): boolean {
  for (const [stat, threshold] of Object.entries(p.requirements)) {
    if (baseStats[stat as keyof CharacterStats] < (threshold ?? 0)) return false;
  }
  return true;
}

// All don ids currently unlocked by the given base stats.
export function unlockedPassiveIds(baseStats: CharacterStats): string[] {
  return runtimePassives.filter((p) => isPassiveUnlocked(p, baseStats)).map((p) => p.id);
}

// Is a specific effect ACTIVE for the player right now (unlocked AND not in the
// player's disabled set). Server hooks call this. `disabled` may be empty.
export function isEffectActive(
  effectKey: PassiveEffectKey,
  baseStats: CharacterStats,
  disabled: ReadonlySet<string> = new Set(),
): boolean {
  for (const p of runtimePassives) {
    if (p.effectKey !== effectKey) continue;
    if (disabled.has(p.id)) continue;
    if (isPassiveUnlocked(p, baseStats)) return true;
  }
  return false;
}

// Zod schema for admin publish validation.
const StatReqSchema = z
  .object({
    str: z.number().int().min(1).max(2000).optional(),
    dex: z.number().int().min(1).max(2000).optional(),
    con: z.number().int().min(1).max(2000).optional(),
    int: z.number().int().min(1).max(2000).optional(),
    fai: z.number().int().min(1).max(2000).optional(),
    rap: z.number().int().min(1).max(2000).optional(),
  })
  .strict();

export const PassiveTemplateSchema = z
  .object({
    id: z.string().min(1).max(48),
    name: z.string().min(1).max(48),
    desc: z.string().min(1).max(240),
    requirements: StatReqSchema,
    effectKey: z.enum([
      "iron_shoulder",
      "light_step",
      "iron_stomach",
      "scholars_eye",
      "soothing_presence",
      "initiative",
      "crusade",
      "colossus_leap",
    ]),
  })
  .strict();

export const PassiveCatalogueSchema = z.array(PassiveTemplateSchema).max(64);
